/**
 * Amharic catalog (Ethiopic / Geʽez script) for the Phase 2 `MessageId` set — Agent 9, batch 3.
 *
 * `dir: 'ltr'`. Native-grade Amharic (አማርኛ) at a mathematician's register. This batch adds the
 * Ethiopic writing system: `scriptOf('am')` → `ethiopic` (new), paired with a Noto-led Ethiopic
 * font stack. **Warrant → «ማረጋገጫ»** (justification/warrant — flagged). Scientific loanwords
 * (logic → ሎጂክ, modal → ሞዳል, heuristic → ሂዩሪስቲክ) are used where Amharic standardly borrows.
 * W/M grades stay Latin (`::numbering-system/latn`). CLDR cardinal categories are one / other
 * (Amharic `one` covers 0–1), so `frameActive` declares those (plus an explicit `=0`). Per
 * INV-STABLE-ID a wrong translation is a presentation concern only — verdicts stay fixed.
 */
import { P2 } from '../message-ids-phase2.js';
export const AM_CATALOG = {
    locale: 'am',
    dir: 'ltr',
    messages: {
        [P2.inquiryTitle]: 'ምርመራ',
        [P2.sectionClaims]: 'አባባሎች',
        [P2.sectionIntegrity]: 'ሙሉነት',
        [P2.axisWarrantLabel]: 'ማረጋገጫ',
        [P2.axisWarrantValue]: 'W{w, number, ::numbering-system/latn}',
        [P2.axisEmpiricalLabel]: 'ተጨባጭ',
        [P2.axisEmpiricalValue]: '{kind, select, defn {ትርጓሜ} interp {ማብራሪያ} testable {ሊፈተሽ የሚችል} tested {ተፈትኗል} refuted {ውድቅ የተደረገ} program {የምርምር መርሐ ግብር} philos {ፍልስፍናዊ} other {ያልታወቀ}}',
        [P2.axisModalLabel]: 'ሞዳል',
        [P2.axisModalValue]: 'M{m, number, ::numbering-system/latn}',
        [P2.axisKidSummary]: 'የጥንካሬ ደረጃ {w, number, ::numbering-system/latn}',
        [P2.stateExplorable]: 'ሊዳሰስ የሚችል',
        [P2.stateRefuted]: 'ውድቅ የተደረገ',
        [P2.stateOutside]: 'ከወሰን ውጭ',
        [P2.verdictConflict]: 'ከ{constraints} ጋር ይጋጫል',
        [P2.verdictOutside]: '{missing} ያስፈልጋል',
        [P2.lossRegisterPlain]: 'ወደ ቀላል ቋንቋ ተቃሎ — የተወሰነ ትክክለኛነት ተትቷል።',
        [P2.lossRegisterKid]: 'ለትንንሽ አንባቢዎች ተቃሎ — ብዙ ዝርዝሮች ተትተዋል።',
        [P2.lossProfileDetail]: 'ሙሉ የማረጋገጫ / ተጨባጭ / ሞዳል መገለጫ እዚህ ተጠቃሏል።',
        [P2.lossConflictDetail]: 'የግጭቱ ዝርዝሮች በዚህ እይታ ተኮማትረዋል።',
        [P2.laneVerified]: 'ተረጋግጧል',
        [P2.laneUnverified]: 'አልተረጋገጠም',
        [P2.laneConflict]: 'ግጭት ተረጋግጧል',
        [P2.constraintLogic]: 'ሎጂክ',
        [P2.constraintLightBends]: 'ብርሃን በስበት መስክ ውስጥ ይጣመማል',
        [P2.constraintParallelPostulate]: 'የትይዩ መስመሮች ፖስቱላት',
        [P2.claimScalarGravity]: 'የስኬላር ስበት ንድፈ ሐሳብ',
        [P2.claimNonEuclidean]: 'ኢ-ኤውክሊዳዊ ጂኦሜትሪ',
        [P2.claimSubstrateSr]: 'የተለየ አንጻራዊነት የንዑስ ክፍል ትርጓሜ',
        [P2.edgeExtObservation]: 'ውጫዊ ምልከታ',
        [P2.edgeResourceSample]: 'የተጋራ የስሌት ሀብት',
        [P2.edgeHeuristicSample]: 'ሂዩሪስቲክ መመሪያ',
        [P2.edgeSharedSample]: 'የተጋራ መሠረት',
        // --- Phase 2 view-chrome ---
        [P2.sectionConstraints]: 'ገደቦች',
        [P2.sectionEdges]: 'ጠርዞች',
        [P2.sectionFrame]: 'ማዕቀፍ',
        [P2.sectionExposure]: 'መጋለጥ',
        [P2.statusAdoptedFormal]: 'ተቀባይነት አግኝቷል (መደበኛ)',
        [P2.statusAdoptedInterpretive]: 'ተቀባይነት አግኝቷል (ተርጓሚ)',
        [P2.statusSuspended]: 'ታግዷል',
        [P2.edgeEffectConstraint]: 'ገደብ ይፈጥራል (ውድቅ ሊያደርግ ይችላል)',
        [P2.edgeEffectResource]: 'ሀብት — በፍርዶች ላይ ተጽዕኖ የለውም',
        [P2.edgeEffectHeuristic]: 'ሂዩሪስቲክ — በፍርዶች ላይ ተጽዕኖ የለውም',
        [P2.edgeEffectShared]: 'የተጋራ መሠረት — በፍርዶች ላይ ተጽዕኖ የለውም',
        [P2.frameActive]: '{count, plural, =0 {ምንም ንቁ ገደብ የለም} one {# ንቁ ገደብ} other {# ንቁ ገደቦች}}',
        [P2.exposureNone]: 'ምንም ተርጓሚ ጥገኝነት የለም',
        [P2.exposureDep]: 'በ{id} ላይ ይመሰረታል',
        [P2.laneTrackMachine]: 'ማሽን',
        [P2.laneTrackExpert]: 'ባለሙያ',
        [P2.laneTrackCommunity]: 'ማህበረሰብ',
        [P2.expertAudited]: 'ተመርምሯል',
        [P2.expertInProgress]: 'በሂደት ላይ',
        [P2.expertNotReviewed]: 'አልተገመገመም',
        [P2.communityFlags]: 'ምልክቶች አሉ',
        [P2.communityNone]: 'ምልክቶች የሉም',
        [P2.controlChallenge]: 'ተቃወም',
        [P2.controlActivate]: 'አንቃ',
        [P2.controlMakeInterpretive]: 'ተርጓሚ አድርግ',
        [P2.controlImportEdge]: 'ጠርዝ አስገባ',
        [P2.controlPromote]: 'አሳድግ',
        [P2.controlUndo]: 'ቀልብስ',
        [P2.controlRedo]: 'ድገም',
        [P2.controlRegister]: 'መዝግብ',
        [P2.promoteRejected]: 'ማሳደግ ተቀባይነት አላገኘም',
        [P2.promoteAccepted]: 'ማሳደግ ተቀባይነት አግኝቷል',
        [P2.promoteReasonRefutationTerminal]: 'ማረጋገጫ ውድቅ መደረግን ሊፈውስ አይችልም።',
        [P2.promoteReasonKeystone]: 'ቁልፍ ማሳደግ ተጨባጭ ድጋፍ ይፈልጋል።',
        [P2.promoteReasonEmpiricalNotFixed]: 'ተጨባጩ ዓይነት አልተወሰነም።',
        [P2.promoteReasonModalNotFixed]: 'የሞዳል ደረጃ አልተወሰነም።',
        [P2.promoteReasonWarrantNotImproved]: 'የማረጋገጫ ደረጃ አልተሻሻለም።',
        [P2.evidenceComputation]: "{engine} computed {op} = {result} under {config}",
        [P2.lossNumericRounding]: "Result rounded to {dp} decimal places",
    },
};
//# sourceMappingURL=am.js.map